/**
 * Phase 21.4 Batch 12 — 🏆 Unified Contest Control Center
 *
 * Single admin surface to manage a contest end-to-end:
 *   - Entries table w/ real covers + inline audio preview
 *   - Approve / reject / admin notes / pick winners (dry-run + commit)
 *   - Historical Attach panel (existing tracks/beats → contest entries)
 *   - Repair Covers toolbar (idempotent HoF cover resolver)
 *   - Reward Status Matrix (per-winner coins/promo/badges/placement/HoF/notify)
 *   - Retry Failed Steps (re-runs commit; award pipeline is idempotent)
 *
 * SAFETY: No cash payouts, no Stripe. Reward coins remain
 * reward_balance only. Every retry is idempotent via dedup_key +
 * $addToSet writes downstream (see contest_rewards.py).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SharedNavigation from "@/components/SharedNavigation";
import ArtistPicker from "@/components/admin/ArtistPicker";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_TONES = {
  pending: "bg-white/10 text-white/80",
  approved: "bg-emerald-500/20 text-emerald-200",
  rejected: "bg-rose-500/20 text-rose-200",
  winner: "bg-amber-500/20 text-amber-200",
  runner_up: "bg-purple-500/20 text-purple-200",
  withdrawn: "bg-white/5 text-gray-400",
};

const OkPill = ({ ok, label, testId }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black ${
      ok
        ? "bg-emerald-500/20 border border-emerald-500/50 text-emerald-100"
        : "bg-rose-500/15 border border-rose-500/50 text-rose-100"
    }`}
    data-testid={testId}
  >
    <span>{ok ? "✓" : "✗"}</span>
    {label}
  </span>
);

const EntryCover = ({ entry, playingId, onPlayToggle }) => {
  const snap = entry.ref_snapshot || {};
  const cover = snap.cover_art;
  const audio = snap.audio_url;
  const isPlaying = playingId === entry.entry_id;
  const placeholder = entry.entry_type === "beat" ? "🎧" : "🎤";
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-12 h-12 rounded-md overflow-hidden bg-gray-800 border border-white/10 flex-shrink-0">
        {cover ? (
          <img
            src={cover}
            alt=""
            className="w-full h-full object-cover"
            data-testid={`admin-entry-cover-${entry.entry_id}`}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xl">{placeholder}</div>
        )}
        {audio && (
          <button
            onClick={() => onPlayToggle(entry.entry_id)}
            className="absolute inset-0 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white text-lg"
            title={isPlaying ? "Pause preview" : "Play preview"}
            data-testid={`admin-entry-play-${entry.entry_id}`}
          >
            {isPlaying ? "⏸" : "▶"}
          </button>
        )}
      </div>
    </div>
  );
};

const AdminContestEntriesPage = () => {
  const { contestId } = useParams();
  const [contest, setContest] = useState(null);
  const [entries, setEntries] = useState([]);
  const [badges, setBadges] = useState({}); // badge_id -> catalog doc
  const [rewardStatus, setRewardStatus] = useState(null); // {contest, rows}
  const [loading, setLoading] = useState(true);

  // pick-winners state: entry_id -> position (1..N)
  const [picks, setPicks] = useState({});
  const [alsoParticipation, setAlsoParticipation] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run result
  const [committing, setCommitting] = useState(false);

  // Audio preview
  const [playingId, setPlayingId] = useState(null);
  const [audioEl, setAudioEl] = useState(null);

  // Repair covers
  const [repairBusy, setRepairBusy] = useState(false);

  // Historical attach
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachType, setAttachType] = useState("track");
  const [attachUser, setAttachUser] = useState({ user_id: "", artist_name: "" });
  const [attachRefId, setAttachRefId] = useState("");
  const [attachNote, setAttachNote] = useState("");
  const [attachBusy, setAttachBusy] = useState(false);
  // iter_221 — Catalog picker replaces manual ID entry.
  const [attachCatalog, setAttachCatalog] = useState([]);
  const [attachCatalogLoading, setAttachCatalogLoading] = useState(false);

  // Retry
  const [retryBusy, setRetryBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [contestsRes, entriesRes, badgesRes] = await Promise.all([
        api.get("/admin/contests"),
        api.get(`/admin/contests/${contestId}/entries`),
        api.get("/admin/badges"),
      ]);
      const found = (contestsRes.data?.contests || []).find((c) => c.contest_id === contestId);
      setContest(found || null);
      setEntries(entriesRes.data?.entries || []);
      const map = {};
      for (const b of badgesRes.data?.badges || []) map[b.badge_id] = b;
      setBadges(map);
    } catch (e) {
      toast.error("Failed to load entries");
    } finally {
      setLoading(false);
    }
  }, [contestId]);

  const loadRewardStatus = useCallback(async () => {
    try {
      const r = await api.get(`/admin/contests/${contestId}/reward-status`);
      setRewardStatus(r.data || null);
    } catch (e) {
      // silent — endpoint may 404 in older deployments
      setRewardStatus(null);
    }
  }, [contestId]);

  useEffect(() => {
    load();
    loadRewardStatus();
  }, [load, loadRewardStatus]);

  // Refresh reward-status when the commit event fires from other tabs / self.
  useEffect(() => {
    const handler = (ev) => {
      if (!ev?.detail?.contestId || ev.detail.contestId === contestId) {
        loadRewardStatus();
        load();
      }
    };
    window.addEventListener("rgm.contest.committed", handler);
    return () => window.removeEventListener("rgm.contest.committed", handler);
  }, [contestId, load, loadRewardStatus]);

  // Stop audio on unmount / entry change
  useEffect(() => {
    return () => {
      if (audioEl) {
        try { audioEl.pause(); } catch (_e) { /* ignore */ }
      }
    };
  }, [audioEl]);

  // iter_221 — Load the selected artist's track or beat catalog for the
  // Attach Historical Entry picker. Fires whenever the artist or the
  // entry type changes. Clears the current selection so the admin has
  // to explicitly re-pick after switching.
  useEffect(() => {
    if (!attachOpen) return;
    setAttachRefId("");
    if (!attachUser.user_id) {
      setAttachCatalog([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setAttachCatalogLoading(true);
      try {
        // iter_221 — Admin catalog endpoint returns every eligible
        // track/beat owned by this artist regardless of moderation
        // status (excludes hard-deleted/hidden). Admin doesn't need
        // to know raw ref_ids.
        const r = await api.get(`/admin/users/${attachUser.user_id}/catalog`, {
          params: { entry_type: attachType, limit: 200 },
        });
        if (!cancelled) setAttachCatalog(r.data?.items || []);
      } catch {
        if (!cancelled) setAttachCatalog([]);
      } finally {
        if (!cancelled) setAttachCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [attachOpen, attachType, attachUser.user_id]);

  const onPlayToggle = (entryId) => {
    const entry = entries.find((e) => e.entry_id === entryId);
    const url = entry?.ref_snapshot?.audio_url;
    if (!url) {
      toast.error("No audio available for this entry");
      return;
    }
    if (playingId === entryId && audioEl) {
      audioEl.pause();
      setPlayingId(null);
      return;
    }
    if (audioEl) {
      try { audioEl.pause(); } catch (_e) { /* ignore */ }
    }
    const el = new Audio(url);
    el.play().catch(() => toast.error("Preview playback failed"));
    el.addEventListener("ended", () => setPlayingId(null));
    setAudioEl(el);
    setPlayingId(entryId);
  };

  const winnerSlots = contest?.winner_slots || 3;
  const positionOptions = winnerSlots === 1 ? [1] : [1, 2, 3];

  const patchEntry = async (entryId, data) => {
    try {
      await api.patch(`/admin/contests/${contestId}/entries/${entryId}`, data);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const togglePick = (entryId, position) => {
    setPicks((p) => {
      if (p[entryId] === position) {
        const { [entryId]: _, ...rest } = p;
        return rest;
      }
      const cleared = { ...p };
      for (const eid of Object.keys(cleared)) {
        if (cleared[eid] === position) delete cleared[eid];
      }
      cleared[entryId] = position;
      return cleared;
    });
    setPreview(null);
  };

  const buildWinnersPayload = () =>
    Object.entries(picks).map(([entry_id, position]) => ({ entry_id, position: Number(position) }));

  const doPreview = async () => {
    const winners = buildWinnersPayload();
    if (winners.length === 0) return toast.error("Pick at least one winner first");
    try {
      const r = await api.post(`/admin/contests/${contestId}/pick-winners`, {
        winners,
        also_award_participation: alsoParticipation,
        dry_run: true,
      });
      setPreview(r.data);
      toast.success("Preview ready — review below, then commit");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const doCommit = async () => {
    const winners = buildWinnersPayload();
    if (winners.length === 0) return toast.error("Pick at least one winner first");
    if (
      !window.confirm(
        "Commit awards? This grants reward coins, promo plays, badges, and Hall of Champions entries. Idempotent — safe to re-run.",
      )
    )
      return;
    setCommitting(true);
    try {
      const r = await api.post(`/admin/contests/${contestId}/pick-winners`, {
        winners,
        also_award_participation: alsoParticipation,
        dry_run: false,
      });
      const data = r.data || {};
      const reportsList = data.reports || [];
      const anyErrors = reportsList.some((rep) => (rep.errors || []).length > 0);
      if (data.partial_success) {
        toast.error(`⚠️ Partial commit — some rewards failed. Contest kept OPEN. Retry (safe).`, {
          description:
            reportsList
              .map((rep) => (rep.errors || []).join("; "))
              .filter(Boolean)
              .slice(0, 3)
              .join(" · ") || "See console for details",
          duration: 8000,
        });
        console.warn("[commit-awards] partial_success reports:", reportsList);
      } else if (anyErrors) {
        toast.warning(`Committed ${reportsList.length} awards — some had non-critical warnings`, {
          description: "See console for details",
          duration: 6000,
        });
        console.warn("[commit-awards] warnings:", reportsList);
      } else {
        toast.success(`✅ Committed — ${reportsList.length} awards granted`);
      }
      setPicks({});
      setPreview(null);
      try {
        window.dispatchEvent(
          new CustomEvent("rgm.contest.committed", {
            detail: { contestId, partial: !!data.partial_success },
          }),
        );
      } catch (_e) { /* ignore */ }
      load();
      loadRewardStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const doRepairCovers = async () => {
    if (repairBusy) return;
    setRepairBusy(true);
    try {
      const r = await api.post("/admin/hall-of-champions/repair-covers");
      const d = r.data || {};
      const rep = d.repaired_count || 0;
      const ok = d.already_valid_count || 0;
      const un = d.unresolved_count || 0;
      // iter_221 — surface the exact result shape so admin knows which
      // rows still need attention.
      const parts = [
        `🖼 Repaired ${rep}`,
        `${ok} already valid`,
        `${un} unresolved`,
      ];
      if (un > 0 && Array.isArray(d.unresolved)) {
        const names = d.unresolved
          .slice(0, 3)
          .map((u) => u.artist_name || u.user_id || u.hof_id)
          .filter(Boolean);
        const suffix = d.unresolved.length > 3 ? ` +${d.unresolved.length - 3} more` : "";
        toast.warning(
          `${parts.join(" · ")}. Unresolved: ${names.join(", ")}${suffix}`,
          { duration: 8000 },
        );
        console.warn("[HoF Repair] Unresolved rows:", d.unresolved);
      } else {
        toast.success(parts.join(" · "), { duration: 5000 });
      }
      loadRewardStatus();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally {
      setRepairBusy(false);
    }
  };

  const doHistoricalAttach = async () => {
    if (!attachUser.user_id) return toast.error("Pick an artist first");
    if (!attachRefId.trim()) return toast.error(`Pick a ${attachType} from the artist's catalog`);
    setAttachBusy(true);
    try {
      const r = await api.post(`/admin/contests/${contestId}/historical-attach`, {
        entry_type: attachType,
        ref_id: attachRefId.trim(),
        user_id: attachUser.user_id,
        note: attachNote.trim() || undefined,
      });
      const d = r.data || {};
      if (d.created) {
        toast.success(`✅ Attached ${attachType} to contest`);
      } else {
        toast.info(`Already attached — no changes (${d.reason || "duplicate"})`);
      }
      setAttachRefId("");
      setAttachNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally {
      setAttachBusy(false);
    }
  };

  const doRetryFailedSteps = async () => {
    const rows = rewardStatus?.rows || [];
    const failed = rows.filter(
      (r) =>
        !r.coins_ok ||
        !r.promo_plays_ok ||
        !r.badges_ok ||
        !r.placement_ok ||
        !r.hall_of_champions_created,
    );
    if (failed.length === 0) return toast.info("Nothing to retry — all steps completed.");
    if (
      !window.confirm(
        `Retry rewards for ${failed.length} winner${failed.length === 1 ? "" : "s"}? Idempotent — safe.`,
      )
    )
      return;
    setRetryBusy(true);
    try {
      const winners = failed.map((r) => ({ entry_id: r.entry_id, position: r.position }));
      const r = await api.post(`/admin/contests/${contestId}/pick-winners`, {
        winners,
        also_award_participation: false,
        dry_run: false,
      });
      const d = r.data || {};
      if (d.partial_success) {
        toast.error("Some reward steps still failing — see console");
        console.warn("[retry-failed] partial:", d.reports);
      } else {
        toast.success(`🔁 Retry completed — ${(d.reports || []).length} winner(s) reprocessed`);
      }
      try {
        window.dispatchEvent(
          new CustomEvent("rgm.contest.committed", {
            detail: { contestId, partial: !!d.partial_success },
          }),
        );
      } catch (_e) { /* ignore */ }
      loadRewardStatus();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetryBusy(false);
    }
  };

  const badgeLabel = (bid) => badges[bid]?.label || bid;

  const anyRetryTargets = useMemo(() => {
    return (rewardStatus?.rows || []).some(
      (r) =>
        !r.coins_ok ||
        !r.promo_plays_ok ||
        !r.badges_ok ||
        !r.placement_ok ||
        !r.hall_of_champions_created,
    );
  }, [rewardStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <p className="p-8 text-gray-500" data-testid="admin-contest-entries-loading">
          Loading entries…
        </p>
      </div>
    );
  }
  if (!contest) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <p className="p-8 text-rose-300">Contest not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-contest-entries-page">
      <SharedNavigation />
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Link to="/admin/contests" className="text-xs text-gray-400 hover:text-white">
              ← Back to contests
            </Link>
            <h1 className="text-2xl font-black">🏆 {contest.title} — Control Center</h1>
            <p className="text-xs text-gray-500 font-mono">
              {contest.contest_id} · status: {contest.status} · winner_slots: {winnerSlots}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={doRepairCovers}
              disabled={repairBusy}
              variant="outline"
              className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 text-xs px-3 py-1 h-auto"
              data-testid="admin-cc-repair-covers-btn"
            >
              {repairBusy ? "🔄 Repairing…" : "🖼 Repair covers"}
            </Button>
            <Button
              onClick={() => setAttachOpen((v) => !v)}
              variant="outline"
              className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 text-xs px-3 py-1 h-auto"
              data-testid="admin-cc-attach-toggle-btn"
            >
              📎 {attachOpen ? "Hide attach panel" : "Attach historical entry"}
            </Button>
            <Link
              to={`/admin/contests/${contestId}`}
              className="text-xs text-purple-300 hover:underline"
              data-testid="admin-entries-edit-contest"
            >
              Edit contest →
            </Link>
          </div>
        </div>

        {/* Historical Attach panel */}
        {attachOpen && (
          <section
            className="rounded-xl border border-cyan-500/40 bg-cyan-500/[0.05] p-4 space-y-3"
            data-testid="admin-cc-attach-panel"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-black text-cyan-100">📎 Attach historical entry</h2>
              <span className="text-[10px] text-cyan-300/70 uppercase tracking-widest">
                imported=true · no rewards granted here
              </span>
            </div>
            <p className="text-xs text-gray-400">
              Attaches an existing track or beat to this contest as an approved entry. Use the
              normal pick-winners flow afterwards to award rewards.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
                  Entry type
                </label>
                <select
                  value={attachType}
                  onChange={(e) => setAttachType(e.target.value)}
                  className="w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                  data-testid="admin-cc-attach-entry-type"
                >
                  <option value="track" className="bg-[#13131a]">🎤 Track</option>
                  <option value="beat" className="bg-[#13131a]">🎧 Beat</option>
                </select>
              </div>
              <div>
                <ArtistPicker
                  value={attachUser.user_id}
                  onChange={setAttachUser}
                  testIdPrefix="admin-cc-attach-artist"
                  label="Artist"
                />
              </div>
            </div>
            {/* iter_221 — Catalog picker replaces the manual `sub_…` /
                `beat_…` ID input. Once an artist is selected, load their
                eligible items and show title + cover art thumbnails.
                The internal ref_id is stored automatically on selection. */}
            {attachUser.user_id && (
              <div className="space-y-2" data-testid="admin-cc-attach-catalog">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block">
                    Pick a {attachType === "beat" ? "beat" : "song"} from {attachUser.artist_name || "this artist"}&apos;s catalog
                  </label>
                  {attachRefId && (
                    <button
                      type="button"
                      onClick={() => setAttachRefId("")}
                      className="text-[10px] text-cyan-300 hover:text-cyan-100 underline"
                      data-testid="admin-cc-attach-clear-selection"
                    >
                      Clear selection
                    </button>
                  )}
                </div>
                {attachCatalogLoading ? (
                  <p className="text-xs text-gray-400" data-testid="admin-cc-attach-catalog-loading">Loading catalog…</p>
                ) : attachCatalog.length === 0 ? (
                  <p className="text-xs text-amber-200" data-testid="admin-cc-attach-catalog-empty">
                    {attachUser.artist_name || "This artist"} has no {attachType === "beat" ? "public beats" : "public tracks"} yet.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="admin-cc-attach-catalog-grid">
                    {attachCatalog.map((item) => {
                      const selected = attachRefId === item.ref_id;
                      return (
                        <button
                          key={item.ref_id}
                          type="button"
                          onClick={() => setAttachRefId(item.ref_id)}
                          className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-all ${
                            selected
                              ? "border-cyan-400/70 bg-cyan-400/10"
                              : "border-white/10 bg-black/40 hover:border-cyan-500/40 hover:bg-white/5"
                          }`}
                          data-testid={`admin-cc-attach-catalog-item-${item.ref_id}`}
                        >
                          <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-800 shrink-0">
                            {item.cover_art ? (
                              <img src={item.cover_art} alt={item.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-lg">{attachType === "beat" ? "🎧" : "🎤"}</div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-xs font-black truncate" data-testid={`admin-cc-attach-catalog-title-${item.ref_id}`}>{item.title}</p>
                            <p className="text-[10px] text-gray-400">
                              {selected ? "✓ Selected" : "Tap to select"}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {/* Selection preview so admin sees exactly what will be attached */}
                {attachRefId && (
                  <div className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 p-2 flex items-center gap-3" data-testid="admin-cc-attach-preview">
                    {(() => {
                      const picked = attachCatalog.find((x) => x.ref_id === attachRefId);
                      if (!picked) return null;
                      return (
                        <>
                          <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-800 shrink-0">
                            {picked.cover_art ? (
                              <img src={picked.cover_art} alt={picked.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-sm">{attachType === "beat" ? "🎧" : "🎤"}</div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] uppercase tracking-widest text-cyan-200 font-black">Attaching</p>
                            <p className="text-xs text-white font-black truncate" data-testid="admin-cc-attach-preview-title">{picked.title}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
                Admin note (optional)
              </label>
              <Textarea
                value={attachNote}
                onChange={(e) => setAttachNote(e.target.value)}
                placeholder="Why is this being attached historically?"
                className="bg-white/5 border-white/10 text-white text-xs h-16"
                data-testid="admin-cc-attach-note"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={doHistoricalAttach}
                disabled={attachBusy || !attachUser.user_id || !attachRefId}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-black h-8 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-cc-attach-submit"
              >
                {attachBusy ? "Attaching…" : "Attach entry"}
              </Button>
            </div>
          </section>
        )}

        {/* Reward Status Matrix — surfaces when there is at least one winner */}
        {rewardStatus?.rows?.length > 0 && (
          <section
            className="rounded-xl border border-amber-500/40 bg-amber-500/[0.05] p-4 space-y-3"
            data-testid="admin-cc-reward-status"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="text-sm font-black text-amber-100">
                🎯 Reward Status — {rewardStatus.rows.length} winner
                {rewardStatus.rows.length === 1 ? "" : "s"}
              </h2>
              <div className="flex items-center gap-2">
                <Button
                  onClick={loadRewardStatus}
                  variant="outline"
                  className="border-white/20 text-white/80 hover:bg-white/10 text-xs px-3 py-1 h-auto"
                  data-testid="admin-cc-reward-status-refresh"
                >
                  🔄 Refresh
                </Button>
                {anyRetryTargets && (
                  <Button
                    onClick={doRetryFailedSteps}
                    disabled={retryBusy}
                    className="bg-amber-400 hover:bg-amber-300 text-black font-black h-8 text-xs"
                    data-testid="admin-cc-retry-failed-btn"
                  >
                    {retryBusy ? "Retrying…" : "🔁 Retry failed steps (idempotent)"}
                  </Button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-gray-400 uppercase tracking-wider border-b border-white/10 text-[10px]">
                  <tr>
                    <th className="py-2 pr-3">Pos</th>
                    <th className="py-2 pr-3">Artist / Entry</th>
                    <th className="py-2 pr-3">Coins</th>
                    <th className="py-2 pr-3">Promo</th>
                    <th className="py-2 pr-3">Badges</th>
                    <th className="py-2 pr-3">Placement</th>
                    <th className="py-2 pr-3">HoF</th>
                    <th className="py-2 pr-3">Notify</th>
                  </tr>
                </thead>
                <tbody>
                  {rewardStatus.rows.map((r) => (
                    <tr
                      key={r.entry_id}
                      className="border-b border-white/5 align-top"
                      data-testid={`admin-cc-reward-row-${r.entry_id}`}
                    >
                      <td className="py-2 pr-3 font-black text-amber-200 text-base">
                        {r.position === 1 ? "🥇" : r.position === 2 ? "🥈" : r.position === 3 ? "🥉" : `#${r.position}`}
                      </td>
                      <td className="py-2 pr-3">
                        <p className="text-white font-black">{r.artist?.artist_name || r.artist?.user_id}</p>
                        <p className="text-[10px] text-gray-400">{r.entry?.ref_snapshot?.title || r.entry?.ref_id}</p>
                      </td>
                      <td className="py-2 pr-3">
                        <OkPill
                          ok={r.coins_ok}
                          label={`${r.coins_granted}/${r.coins_configured}`}
                          testId={`admin-cc-reward-coins-${r.entry_id}`}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <OkPill
                          ok={r.promo_plays_ok}
                          label={
                            r.promo_plays_configured === 0
                              ? "n/a"
                              : `${r.promo_plays_granted}/${r.promo_plays_configured}`
                          }
                          testId={`admin-cc-reward-promo-${r.entry_id}`}
                        />
                        {r.promo_skip_reason && !r.promo_plays_ok && (
                          <p
                            className="mt-1 text-[9px] text-rose-300"
                            data-testid={`admin-cc-reward-promo-reason-${r.entry_id}`}
                          >
                            {r.promo_skip_reason.replace(/_/g, " ")}
                          </p>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {(r.badges_configured || []).length === 0 ? (
                          <span className="text-gray-500 text-[10px]">none configured</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(r.badges_configured || []).map((bid) => {
                              const attached = (r.badges_attached_on_user || []).includes(bid);
                              return (
                                <OkPill
                                  key={bid}
                                  ok={attached}
                                  label={badgeLabel(bid)}
                                  testId={`admin-cc-reward-badge-${r.entry_id}-${bid}`}
                                />
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <OkPill
                          ok={r.placement_ok}
                          label={r.placement_tag || "—"}
                          testId={`admin-cc-reward-placement-${r.entry_id}`}
                        />
                        {!r.placement_ok && (
                          <p className="mt-1 text-[9px] text-gray-500">
                            user: {r.placement_attached_on_user ? "✓" : "✗"} · src:{" "}
                            {r.placement_attached_on_source ? "✓" : "✗"}
                          </p>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <OkPill
                          ok={r.hall_of_champions_created}
                          label={r.hall_of_champions_created ? "created" : "missing"}
                          testId={`admin-cc-reward-hof-${r.entry_id}`}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <OkPill
                          ok={r.notification_sent}
                          label={r.notification_sent ? "sent" : "none"}
                          testId={`admin-cc-reward-notify-${r.entry_id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-500">
              Coins live in <span className="text-amber-200">reward_balance</span> only (non-cashout). Every retry is
              idempotent via <code>contest:{"{contest_id}"}:{"{entry_id}"}</code>.
            </p>
          </section>
        )}

        {/* Entries table */}
        <section
          className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
          data-testid="admin-entries-table-section"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-black text-white">Entries ({entries.length})</h2>
            <div className="flex items-center gap-3 text-xs">
              <label
                className="inline-flex items-center gap-2 text-gray-300"
                data-testid="admin-entries-participation-toggle"
              >
                <input
                  type="checkbox"
                  checked={alsoParticipation}
                  onChange={(e) => setAlsoParticipation(e.target.checked)}
                />
                Also grant participation to non-winners
              </label>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-400 text-xs uppercase tracking-wider border-b border-white/10">
                <tr>
                  <th className="py-2 pr-3">Preview</th>
                  <th className="py-2 pr-3">Artist</th>
                  <th className="py-2 pr-3">Entry</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Position</th>
                  <th className="py-2 pr-3">Note / admin</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.entry_id}
                    className="border-b border-white/5 align-top"
                    data-testid={`admin-entry-row-${e.entry_id}`}
                  >
                    <td className="py-2 pr-3">
                      <EntryCover entry={e} playingId={playingId} onPlayToggle={onPlayToggle} />
                    </td>
                    <td className="py-2 pr-3">
                      <p className="text-white text-xs font-black">{e.artist_name || e.user_id}</p>
                      <p className="text-[10px] text-gray-500 font-mono">{e.user_id}</p>
                      {e.imported && (
                        <span
                          className="mt-1 inline-flex text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/40 text-cyan-200 font-black uppercase tracking-wider"
                          data-testid={`admin-entry-imported-${e.entry_id}`}
                        >
                          imported
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-200">
                      <p className="text-white text-xs">{e.ref_snapshot?.title || e.ref_id}</p>
                      <p className="text-[10px] text-gray-500">{e.entry_type}</p>
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                          STATUS_TONES[e.status] || "bg-white/10"
                        }`}
                      >
                        {e.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1" data-testid={`admin-entry-pick-row-${e.entry_id}`}>
                        {positionOptions.map((p) => (
                          <button
                            key={p}
                            onClick={() => togglePick(e.entry_id, p)}
                            className={`w-7 h-7 rounded-full text-xs font-black transition-colors ${
                              picks[e.entry_id] === p
                                ? "bg-amber-400 text-black"
                                : "bg-white/5 text-gray-300 hover:bg-white/10"
                            }`}
                            data-testid={`admin-entry-pick-${e.entry_id}-${p}`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-300 max-w-xs">
                      <p className="text-gray-200 italic">&quot;{e.note || ""}&quot;</p>
                      <Textarea
                        defaultValue={e.admin_notes || ""}
                        placeholder="Admin note…"
                        onBlur={(ev) => {
                          if (ev.target.value !== (e.admin_notes || "")) {
                            patchEntry(e.entry_id, { admin_notes: ev.target.value });
                          }
                        }}
                        className="mt-1 bg-white/5 border-white/10 text-white text-xs h-16"
                        data-testid={`admin-entry-admin-notes-${e.entry_id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          onClick={() => patchEntry(e.entry_id, { status: "approved" })}
                          className="text-xs text-emerald-300 hover:underline"
                          data-testid={`admin-entry-approve-${e.entry_id}`}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => patchEntry(e.entry_id, { status: "rejected" })}
                          className="text-xs text-rose-300 hover:underline"
                          data-testid={`admin-entry-reject-${e.entry_id}`}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-gray-500 text-sm" data-testid="admin-entries-empty">
                      No entries yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pick winners: badge preview + preview + commit */}
        {Object.keys(picks).length > 0 && (
          <section
            className="rounded-xl border border-amber-500/40 bg-amber-500/[0.05] p-5 space-y-4"
            data-testid="admin-pick-winners-panel"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-sm font-black text-amber-100">
                🏆 Pick winners — {Object.keys(picks).length} selected
              </h2>
              <div className="flex gap-2">
                <Button
                  onClick={doPreview}
                  className="bg-white/10 hover:bg-white/20 text-white h-8 text-xs"
                  data-testid="admin-pick-winners-preview"
                >
                  Preview (dry-run)
                </Button>
                <Button
                  onClick={doCommit}
                  disabled={committing}
                  className="bg-amber-400 text-black hover:bg-amber-300 font-black h-8 text-xs"
                  data-testid="admin-pick-winners-commit"
                >
                  {committing ? "Committing…" : "Commit awards"}
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-white/10 p-3 bg-black/40" data-testid="admin-badge-preview">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Badge Preview</p>
              <div className="space-y-2">
                {Object.entries(picks)
                  .sort((a, b) => a[1] - b[1])
                  .map(([eid, pos]) => {
                    const posKey = String(pos);
                    const rewardBadges = contest?.rewards?.[posKey]?.badges || [];
                    const e = entries.find((x) => x.entry_id === eid);
                    return (
                      <div key={eid} className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="font-black text-amber-200">Pos {pos}:</span>
                        <span className="text-white">{e?.artist_name || e?.user_id}</span>
                        {rewardBadges.length === 0 ? (
                          <span className="text-gray-500 italic">no badges configured</span>
                        ) : (
                          rewardBadges.map((bid) => (
                            <span
                              key={bid}
                              className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-100 text-[11px] font-black"
                              data-testid={`admin-badge-preview-chip-${eid}-${bid}`}
                            >
                              {badgeLabel(bid)}
                            </span>
                          ))
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>

            {preview && (
              <div
                className="rounded-lg border border-white/10 p-3 bg-black/40"
                data-testid="admin-pick-winners-preview-result"
              >
                <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
                  Dry-run preview — {preview.reward_bucket}
                </p>
                <table className="w-full text-xs">
                  <thead className="text-left text-gray-500">
                    <tr>
                      <th className="pr-3">Pos</th>
                      <th className="pr-3">User</th>
                      <th className="pr-3">Coins</th>
                      <th className="pr-3">Promo plays</th>
                      <th className="pr-3">Badges</th>
                      <th className="pr-3">HOF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.preview || []).map((p) => (
                      <tr key={p.entry_id} className="border-t border-white/5 text-gray-200">
                        <td className="py-1 pr-3 font-black text-amber-200">{p.position}</td>
                        <td className="py-1 pr-3 font-mono text-[10px]">{p.user_id}</td>
                        <td className="py-1 pr-3">{p.will_grant_coins}</td>
                        <td className="py-1 pr-3">{p.will_grant_promo_plays}</td>
                        <td className="py-1 pr-3">
                          {(p.will_add_badges || []).map((b) => badgeLabel(b)).join(", ") || "—"}
                        </td>
                        <td className="py-1 pr-3">{p.will_add_to_hall_of_champions ? "✓" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminContestEntriesPage;
